<template>
  <div class="uk-modal-dialog uk-modal-body uk-margin-auto-vertical form">
    <Form @submit="onBeforeSubmit" ref="form" :validation-schema="validationSchema" :initial-values="formValue">
      <div class="uk-modal-body">
        <FormError :errors="globalErrors" />
        <table class="uk-table" border="1">
          <thead>
            <tr v-for="(row, rowIndex) in headers" :key="rowIndex">
              <th v-for="(col, colIndex) in row" :colspan="col.colspan" :rowspan="col.rowspan" :key="colIndex" :style="col.style">
                {{ col.text }}<span v-if="col.required" class="uk-text-danger">*</span
                ><HelpIcon
                  v-if="col.helpIconMessage"
                  :tooltipText="col.helpIconMessage"
                  position="top-left"
                  cls="uk-active uk-width-auto"
                />
              </th>
            </tr>
          </thead>
          <tbody>
            <tr class="center">
              <td>
                <Field name="name" v-slot="{ field }"><input class="uk-input" type="text" v-bind="field"/></Field>
              </td>
              <td>
                <Field name="operationRange" v-slot="{ field }">
                  <select class="uk-select" v-bind="field">
                    <option v-for="(value, key) in operationRanges" :key="key" :value="key">
                      {{ value }}
                    </option>
                  </select>
                </Field>
              </td>
              <td class="blue">
                <input
                  type="checkbox"
                  class="checkbox"
                  :checked="formValue.permissions.employee.includes('read')"
                  @change="updateFormValue('employee', 'read')"
                  disabled
                />
              </td>
              <td class="blue">
                <input
                  type="checkbox"
                  class="checkbox"
                  :checked="formValue.permissions.employee.includes('edit')"
                  @change="updateFormValue('employee', 'edit')"
                />
              </td>
              <td class="blue">
                <input
                  type="checkbox"
                  class="checkbox "
                  :checked="formValue.permissions.employeeStats.includes('read')"
                  @change="updateFormValue('employeeStats', 'read')"
                />
              </td>
              <td class="orange">
                <input
                  type="checkbox"
                  class="checkbox"
                  :checked="formValue.permissions.applicant.includes('read')"
                  @change="updateFormValue('applicant', 'read')"
                />
              </td>
              <td class="orange">
                <input
                  type="checkbox"
                  class="checkbox"
                  :checked="formValue.permissions.applicant.includes('edit')"
                  @change="updateFormValue('applicant', 'edit')"
                />
              </td>
              <td class="orange">
                <input
                  type="checkbox"
                  class="checkbox"
                  :checked="formValue.permissions.applicantStats.includes('read')"
                  @change="updateFormValue('applicantStats', 'read')"
                />
              </td>
              <td class="blue">
                <input
                  type="checkbox"
                  class="checkbox"
                  :checked="formValue.permissions.analysis.includes('read')"
                  @change="updateFormValue('analysis', 'read')"
                />
              </td>
              <td class="yellow" colspan="3">
                <input
                  type="checkbox"
                  class="checkbox"
                  :checked="formValue.permissions.asBroadcast.includes('edit')"
                  @change="updateFormValue('asBroadcast', 'edit')"
                />
              </td>
              <td class="green" colspan="3">
                <input
                  type="checkbox"
                  class="checkbox"
                  :checked="formValue.permissions.manage.includes('edit')"
                  @change="updateFormValue('manage', 'edit')"
                />
              </td>
              <td class="pink" colspan="3">
                <input
                  type="checkbox"
                  class="checkbox"
                  :checked="formValue.permissions.specialManage.includes('edit')"
                  @change="updateFormValue('specialManage', 'edit')"
                />
              </td>
            </tr>
          </tbody>
        </table>
        <FieldError name="name" class="uk-margin-small-left" />
        <FieldError name="operationRange" class="uk-margin-small-left" />
      </div>
      <div>
        <div class="uk-modal-footer uk-text-right form-action">
          <div>
            <ActionButton title="戻る" class="uk-modal-close" />
          </div>
          <div class="form-action-main">
            <ActionButton title="確定" color="primary" type="submit" />
            <ActionButton v-if="mode === 'edit'" title="削除" color="warn" @click="onDeleteButtonClick" />
          </div>
        </div>
      </div>
    </Form>
  </div>
</template>
<style lang="scss" scoped>
@include modal-form;
div.uk-modal-dialog {
  width: unset;
}
.uk-table {
  border: thin solid #e5e5e5;
  white-space: nowrap;

  th {
    text-align: center;
    // color: #fc974a
  }

  th,
  td {
    padding: 2px;
    vertical-align: middle;
  }
}
.checkbox {
  width: 28px;
  height: 28px;
}
.center {
  text-align: center;
}
.gray {
  background: #f8f8f8;
}

.blue {
  background: #b8dcff;
}
.lightblue {
  background: lighten($color: #b8dcff, $amount: 4);
}
.orange {
  background: #ffdbbd;
}
.lightorange {
  background: lighten($color: #ffdbbd, $amount: 4);
}
.yellow {
  background: #f9f1d2;
}
.lightyellow {
  background: lighten($color: #f9f1d2, $amount: 4);
}
.green {
  background: #e4f6d5;
}
.lightgreen {
  background: lighten($color: #e4f6d5, $amount: 4);
}
.pink {
  background: #ffe8ef;
}
.lightpink {
  background: lighten($color: #ffe8ef, $amount: 3);
}
</style>
<script lang="ts">
import * as yup from "yup";
import { Options } from "vue-class-component";
import { Prop } from "vue-property-decorator";
import { PropType } from "vue";
import { Field, Form, FormActions } from "vee-validate";
import FormMixin from "../../helpers/mixins/form";
import ActionButton from "../atoms/ActionButton.vue";
import FieldError from "../atoms/FieldError.vue";
import FormError from "../atoms/FormError.vue";
import { AuthApi, OperationRangesEnum } from "../../autogen";
import HelpIcon from "../atoms/HelpIcon.vue";

interface StrKeyObject {
  [key: string]: any;
}

interface FormValue {
  name: string;
  operationRange: keyof typeof OperationRangesEnum | "none";
  permissions: StrKeyObject;
}

@Options({
  components: { ActionButton, Field, Form, FieldError, FormError, HelpIcon }
})
export default class AuthorityForm extends FormMixin {
  globalErrors: { message: string }[] = [];
  validationSchema!: yup.AnyObjectSchema;
  formName = "権限";
  @Prop({ type: String }) mode: "add" | "edit" = "add";
  @Prop({ type: Array as PropType<Array<Object>> }) headers = [];
  formValue: FormValue = {
    name: "",
    operationRange: "none",
    permissions: {
      employee: [],
      applicant: [],
      analysis: [],
      manage: [],
      specialManage: [],
      asBroadcast: [],
      employeeStats: [],
      applicantStats: []
    }
  };
  @Prop({ type: String }) selectedPermissionId = "";

  get operationRanges() {
    return Object.assign({ none: "" }, OperationRangesEnum);
  }

  updateFormValue(type: string, permission: string) {
    if (this.formValue.permissions[type].includes(permission)) {
      this.formValue.permissions[type] = this.formValue.permissions[type].filter((p: string) => p !== permission);
    } else {
      this.formValue.permissions[type].push(permission);
    }
  }

  async onDeleteButtonClick() {
    this.onDelete(async () => {
      await new AuthApi().deleteRole({
        id: this.selectedPermissionId
      });
      this.$emit("delete");
    }, "権限を削除します。よろしいでしょうか？");
  }

  async beforeCreate() {
    this.validationSchema = yup.object({
      name: yup.string().required(this.$t("input_required", { field: "権限名" })),
      operationRange: yup
        .mixed()
        .oneOf(Object.keys(OperationRangesEnum), this.$t("input_required", { field: "範囲" }))
        .required()
    });
  }

  async onBeforeSubmit(value: FormValue) {
    this.formValue.name = value.name;
    this.formValue.operationRange = value.operationRange;
    await this.onSubmit(async () => {
      if (this.mode === "add") {
        await new AuthApi().createRole({
          roleRequest: {
            name: this.formValue.name,
            operationRange: this.formValue.operationRange,
            permissions: this.formValue.permissions
          }
        });
      } else if (this.mode === "edit") {
        await new AuthApi().updateRole({
          id: this.selectedPermissionId,
          roleRequest: {
            name: this.formValue.name,
            operationRange: this.formValue.operationRange,
            permissions: this.formValue.permissions
          }
        });
      }
      this.$emit("confirm");
      this.clearForm();
    });
  }
  setFormValue(value: FormValue) {
    this.globalErrors = [];
    this.formValue = value;
  }
  clearForm() {
    if (!this.$refs.form) return;
    const formValidation = this.$refs.form as FormActions<yup.AnyObjectSchema>;
    formValidation.resetForm();
  }
}
</script>
